<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaText from './GaiaText.vue';
import { gaiaFonts, gaiaNeutral, gaiaTracking } from '../themes/tokens';

const props = defineProps({
  /** Optional counter shown next to the label, e.g. "3 of 12". */
  count: {
    type: String,
    default: undefined,
  },
  /** sidebar = the wider-tracked variant used for sidebar sections;
      strip = the tinted full-width group header (review fields);
      rule = label + count with a hairline running to the edge (the run
      detail's result groups). */
  variant: {
    type: String as PropType<'default' | 'sidebar' | 'strip' | 'rule'>,
    default: 'default',
  },
});

// The strip reads as a row header rather than a section label, so it carries
// the darker text tone. `default` sits one step above faint: at the smaller
// size below, `faint` on white is too little contrast to read as a heading —
// it went mushy rather than quiet. `sidebar` stays faint, where the label is
// deliberately recessive against the nav rows under it.
const labelTone = computed(() => {
  if (props.variant === 'strip') return 'text';
  return props.variant === 'default' ? 'subtle' : 'faint';
});

// `micro` is this system's uppercase-label size — `body` is copy, and a label
// set at copy size reads oversized for what it is. `rule` was already the only
// variant drawn at micro; `default` belongs with it. The sidebar and the strip
// keep `body`: both are tuned against the rows they head, not against a page.
const labelSize = computed(() =>
  props.variant === 'rule' || props.variant === 'default' ? 'micro' : 'body'
);

const ruleColor = gaiaNeutral.borderSoft;
const stripBg = gaiaNeutral.surfaceTint;
const stripBorder = gaiaNeutral.borderSubtle;
const monoFont = gaiaFonts.mono;
</script>

<template>
  <!-- Uppercase section labels from mock 5a. naive-ui has no section-header
       primitive, so the row is ours. It stays a plain flex div rather than an
       n-flex: GaiaSidebar hides this whole row when collapsed via
       :deep(.gaia-section-header){display:none}, and n-flex would write
       display:flex inline and out-rank that.

       #extra sits between the label and the count — glyphs that belong to the
       group rather than to any one row (the run detail's fired group actions). -->
  <div class="gaia-section-header" :class="`gaia-section-header--${props.variant}`">
    <gaia-text
      class="label"
      variant="label"
      :size="labelSize"
      :tone="labelTone"
      :style="props.variant === 'sidebar' ? { letterSpacing: '0.1em' } : undefined"
    >
      <slot />
    </gaia-text>
    <slot name="extra" />
    <gaia-text
      v-if="props.count"
      class="count"
      variant="label"
      :size="labelSize"
      weight="semibold"
      tone="muted"
      :style="{ letterSpacing: gaiaTracking.labelSoft }"
    >
      {{ props.count }}
    </gaia-text>
    <div v-if="props.variant === 'rule'" class="rule" />
  </div>
</template>

<style scoped>
.gaia-section-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

/* Fills the row to the right of the label, ruling off the group below it. */
.gaia-section-header--rule {
  align-items: center;
  gap: 9px;
}

.gaia-section-header--rule .rule {
  flex: 1;
  height: 1px;
  background: v-bind(ruleColor);
}

/* The strip spans its container and rules off a group of rows, so it owns the
   tint and hairlines its callers would otherwise repeat. */
.gaia-section-header--strip {
  /* A band across the list rather than a line of type, so anything slotted
     into it (a group's action glyphs) centres on the bar. */
  align-items: center;
  padding: 9px 15px;
  background: v-bind(stripBg);
  border-top: 1px solid v-bind(stripBorder);
  border-bottom: 1px solid v-bind(stripBorder);
}

.gaia-section-header--strip .count {
  margin-left: auto;
  font-family: v-bind(monoFont);
}
</style>
